import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { useRouteMasterStore } from "./RouteMasterStore/useRouteMasterStore";
import RouteModalCloseButton, {
  routeModalHeaderClassName,
} from "./RouteModalCloseButton";
import RouteStoppageMapModalHost from "./RouteStoppageMapModalHost";
import RouteStoppagePointsSection from "./RouteStoppagePointsSection";
import { useRouteVehicleModalData } from "./useRouteVehicleModalData";
import {
  getCurrentAssignedVehicleLabel,
  getVehicleNumber,
} from "./routeVehicleModalUtils";

const ReassignVehicleModal = ({ showModal, setShowModal, editRow }) => {
  const { isSaving, reassignVehicleToRoute } = useRouteMasterStore();
  const [newVehicleId, setNewVehicleId] = useState("");
  const [reassignmentReason, setReassignmentReason] = useState("");

  const {
    vehicles,
    stoppages,
    routeDetail,
    isLoadingRoute,
    activeStoppageIndex,
    setActiveStoppageIndex,
    routePoints,
    assignedVehicles,
    openStoppageMap,
    handleAddStoppage,
    handleCloseStoppageMap,
    handleDeleteStoppage,
    handleSaveStoppage,
    getRouteMeta,
  } = useRouteVehicleModalData({ showModal, editRow });

  useEffect(() => {
    if (!showModal) return;
    setNewVehicleId("");
    setReassignmentReason("");
  }, [showModal, editRow?.routeId]);

  const handleClose = () => {
    setNewVehicleId("");
    setReassignmentReason("");
    setShowModal(false);
  };

  const currentAssignedVehicle =
    getCurrentAssignedVehicleLabel(routeDetail ?? editRow) ||
    assignedVehicles[0]?.vehicleNumber ||
    "";

  const availableVehicles = vehicles.filter((vehicle) => {
    const vehicleNumber = getVehicleNumber(vehicle);
    return (
      vehicleNumber.toLowerCase() !== currentAssignedVehicle.toLowerCase()
    );
  });

  const handleReassign = async () => {
    if (!newVehicleId) {
      toast.error("Please select a new vehicle.");
      return;
    }

    if (!reassignmentReason.trim()) {
      toast.error("Please enter reason for vehicle reassignment.");
      return;
    }

    const selectedVehicle = vehicles.find(
      (vehicle) => String(vehicle.vehicleId) === String(newVehicleId)
    );

    if (!selectedVehicle) {
      toast.error("Selected vehicle is not available.");
      return;
    }

    const result = await reassignVehicleToRoute({
      ...getRouteMeta(),
      assignedVehicleId: selectedVehicle.vehicleId,
      assignedVehicleNumber: getVehicleNumber(selectedVehicle),
      reason: reassignmentReason.trim(),
      stoppages,
      routeCoordinates: routePoints,
    });

    if (result?.success) {
      handleClose();
    }
  };

  return (
    <>
      <Modal
        size="xl"
        show={showModal && activeStoppageIndex === null}
        onHide={handleClose}
        backdrop="static"
        centered
        contentClassName="overflow-visible"
        dialogClassName="overflow-visible"
        aria-labelledby="reassign-vehicle-modal-title"
      >
        <Modal.Header className={routeModalHeaderClassName}>
          <Modal.Title id="reassign-vehicle-modal-title">
            Reassign Vehicle
          </Modal.Title>
          <RouteModalCloseButton onClick={handleClose} />
        </Modal.Header>
        <hr className="mt-4 mb-0" />

        <Modal.Body
          className="pt-3"
          style={{ maxHeight: "calc(100vh - 8rem)", overflowY: "auto" }}
        >
          <div className="row">
            <div className="col-md-4 mb-3">
              <label htmlFor="reassignRouteCode" className="form-label">
                Route Code
              </label>
              <input
                id="reassignRouteCode"
                type="text"
                className="form-control"
                value={routeDetail?.routeCode ?? editRow?.routeCode ?? ""}
                readOnly
              />
            </div>

            <div className="col-md-8 mb-3">
              <label htmlFor="reassignRouteDescription" className="form-label">
                Route Description
              </label>
              <textarea
                id="reassignRouteDescription"
                className="form-control"
                rows={3}
                value={routeDetail?.routeDescription ?? editRow?.routeDescription ?? ""}
                readOnly
              />
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="currentAssignedVehicle" className="form-label">
                Current Assigned Vehicle
              </label>
              <input
                id="currentAssignedVehicle"
                type="text"
                className="form-control"
                value={currentAssignedVehicle}
                readOnly
              />
            </div>

            <div className="col-md-6 mb-3">
              <label htmlFor="newVehicleSelect" className="form-label">
                Select New Vehicle
              </label>
              <select
                id="newVehicleSelect"
                className="form-select"
                value={newVehicleId}
                onChange={(event) => setNewVehicleId(event.target.value)}
              >
                <option value="">Select Vehicle Number</option>
                {availableVehicles.map((vehicle) => (
                  <option key={vehicle.vehicleId} value={vehicle.vehicleId}>
                    {vehicle.otherNo || getVehicleNumber(vehicle)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="reassignmentReason" className="form-label">
              Reason For Reassignment
            </label>
            <textarea
              id="reassignmentReason"
              className="form-control"
              rows={3}
              placeholder="Enter reason for vehicle reassignment"
              value={reassignmentReason}
              onChange={(event) => setReassignmentReason(event.target.value)}
            />
          </div>

          <RouteStoppagePointsSection
            stoppages={stoppages}
            isLoadingRoute={isLoadingRoute}
            onEditStoppage={openStoppageMap}
            onDeleteStoppage={handleDeleteStoppage}
            onAddStoppage={handleAddStoppage}
          />

          <hr className="mt-2 mb-3" />
          <div className="d-flex justify-content-end gap-2 pb-1">
            <button
              type="button"
              className="btn btn-outline-primary px-4"
              onClick={handleClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary px-4"
              onClick={handleReassign}
              disabled={isSaving || isLoadingRoute}
            >
              {isSaving ? "Saving..." : "Reassign"}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <RouteStoppageMapModalHost
        showModal={showModal}
        activeStoppageIndex={activeStoppageIndex}
        setActiveStoppageIndex={setActiveStoppageIndex}
        onCloseStoppageMap={handleCloseStoppageMap}
        routeId={editRow?.routeId || 0}
        routePoints={routePoints}
        stoppages={stoppages}
        onSaveStoppage={handleSaveStoppage}
      />
    </>
  );
};

export default ReassignVehicleModal;
